/**
 * Andes metadata classification constants.
 *
 * Source of truth: the Andes Metadata Tag Inventory wiki, generated from the
 * AndesMetadataDictionaryCDK package.
 *   https://w.amazon.com/bin/view/BDT/Products/AndesDataTagging/TagInventoryList
 *
 * These are the canonical BDT metadata tag IDs (system names) used when
 * classifying columns/fields of an Andes dataset. Values are the exact tag IDs
 * expected by BDT tagging tooling - do not hand-edit them; re-sync from the
 * wiki above if BDT adds or renames tags.
 */

/**
 * Data Classification Level tags (Policy-97 Data Handling Standard).
 * These are derived automatically on a dataset from the data-element tags
 * attached to it, ordered least to most sensitive.
 */
export const DATA_CLASSIFICATION_LEVEL: Readonly<Record<string, string>> = {
    PUBLIC: "tag.data-classification-level.public",
    CONFIDENTIAL: "tag.data-classification-level.confidential",
    HIGHLY_CONFIDENTIAL: "tag.data-classification-level.highly-confidential",
    RESTRICTED: "tag.data-classification-level.restricted",
    CRITICAL: "tag.data-classification-level.critical",
};

/**
 * Data Element tags: standardized names for attributes/columns in Andes
 * datasets, grouped by the Data Category shown in the wiki inventory.
 */
export const DATA_ELEMENT_TAGS = {
    ADVERTISING: {
        ADVERTISER_ID_CLEAR: "tag.data-element.advertiser-id.clear",
        ADVERTISING_ID: "tag.data-element.advertising-id",
        ORDER_ID_PSEUDONYMIZED: "tag.data-element.order-id.pseudonymized",
        ADVERTISING_TEEN_DATA: "tag.data-element.advertising-teen-data",
        _3P_COOKIE_ID_CLEAR: "tag.data-element.3p-cookie-id.clear",
        SIS_DEVICE_ID_PSEUDONYMIZED: "tag.data-element.sis-device-id.pseudonymized",
        SIS_SESSION_ID_PSEUDONYMIZED: "tag.data-element.sis-session-id.pseudonymized",
    },
    ALEXA: {
        CHILD_DIRECTED_FLAG: "tag.data-element.child-directed-flag",
        UTTERANCE_INTERACTION_ID: "tag.data-element.utterance-interaction-id",
    },
    ASSOCIATES: {
        ASSOCIATE_STORE_ID_CLEAR: "tag.data-element.associate-store-id.clear",
    },
    BIOMETRIC: {
        DNA_RNA_SEQUENCING: "tag.data-element.dna-rna-sequencing",
        FACIAL_MAP: "tag.data-element.facial-map",
        FINGERPRINTS: "tag.data-element.fingerprints",
        INDIVIDUAL_APPEARANCE: "tag.data-element.individual-appearance",
        KEYBOARD_DYNAMICS: "tag.data-element.keyboard-dynamics",
        MOUSE_DYNAMICS: "tag.data-element.mouse-dynamics",
        SIGNATURE_IMAGE: "tag.data-element.signature-image",
        VOICE_BIOMETRIC: "tag.data-element.voice-biometric",
    },
    BUSINESS: {
        AMAZON_BUSINESS_GROUP_ID_CLEAR: "tag.data-element.amazon-business-group-id.clear",
        BUSINESS_ACCOUNT_ID: "tag.data-element.business-account-id",
        BWP_OWNER_ID: "tag.data-element.bwp-owner-id",
        HEADLESS_ACCOUNT_ID_CLEAR: "tag.data-element.headless-account-id.clear",
        PARTNER_ACCOUNT_ID: "tag.data-element.partner-account-id",
    },
    COMPLIANCE_OPERATIONS: {
        SOFT_DELETE_INDICATOR: "tag.data-element.soft-delete-indicator",
    },
    CONTENT: {
        SHOPPABLE_CONTENT_ID: "tag.data-element.shoppable-content-id",
    },
    CUSTOMER_CONTENT: {
        UPLOADED_DOCUMENTATION: "tag.data-element.uploaded-documentation",
        WRITING_SAMPLE: "tag.data-element.writing-sample",
    },
    CUSTOMER_IDENTITY: {
        ACCOUNT_NAME: "tag.data-element.account-name",
        AMAZON_LOCKER_ADMIN_ID: "tag.data-element.amazon-locker-admin-id",
        DELETED_CUSTOMER_ID_CLEAR: "tag.data-element.deleted-customer-id.clear",
        DELETED_CUSTOMER_ID_OBFUSCATED: "tag.data-element.deleted-customer-id.obfuscated",
        FAMILY_HOUSEHOLD_DATA: "tag.data-element.family-household-data",
        LOYALTY_PROGRAM_NUMBER: "tag.data-element.loyalty-program-number",
        HOUSEHOLD_ID_CLEAR: "tag.data-element.household-id.clear",
        HOUSEHOLD_ID_PSEUDONYMIZED: "tag.data-element.household-id.pseudonymized",
        RESIDENT_ID: "tag.data-element.resident-id",
        UUID_CLEAR: "tag.data-element.uuid.clear",
    },
    CUSTOMER_SERVICE: {
        AGENT_ID_CLEAR: "tag.data-element.agent-id.clear",
        CARE_TIMESTAMP: "tag.data-element.care-timestamp",
        CONTACT_ID_CLEAR: "tag.data-element.contact-id.clear",
    },
    DEVICE: {
        DEVICE_IP_ADDRESS: "tag.data-element.device-ip-address",
        DEVICE_MAC_ADDRESS: "tag.data-element.device-mac-address",
        DEVICE_ID_OTHER: "tag.data-element.device-id-other",
        PHONE_SERIAL_NUMBER: "tag.data-element.phone-serial-number",
        DEVICE_SERIAL_NUMBER: "tag.data-element.device-serial-number",
        DEVICE_TIMESTAMP: "tag.data-element.device-timestamp",
        FAX_NUMBER: "tag.data-element.fax-number",
        DEVICE_MOBILE_TELEPHONY_ID_CLEAR: "tag.data-element.device-mobile-telephony-id.clear",
        DEVICE_SERIAL_NUMBER_PSEUDONYMIZED: "tag.data-element.device-serial-number.pseudonymized",
        PHONE_NUMBER: "tag.data-element.phone-number",
        TRUNCATED_IP_ADDRESS: "tag.data-element.truncated-ip-address",
    },
    DMA: {
        LOB_EXPRESSION_BITMAP: "tag.data-element.lob-expression-bitmap",
        LOB_EXPRESSION: "tag.data-element.lob-expression",
    },
    DRDS: {
        RTBF_REQUEST_DATE: "tag.data-element.rtbf-request-date",
    },
    EMPLOYEE: {
        EMPLOYEE_MIDWAY_ID: "tag.data-element.employee-midway-id",
    },
    FINANCE: {
        ACCOUNT_TYPE: "tag.data-element.account-type",
        ACCURAL_REVERSE_PERIOD_NAME: "tag.data-element.accural-reverse-period-name",
        ADJUSTMENT_ITEM_ID: "tag.data-element.adjustment-item-id",
        ADJUSTMENT_TYPE: "tag.data-element.adjustment-type",
        ADVERTISING_FEE: "tag.data-element.advertising-fee",
        FULFILLMENT_FEE: "tag.data-element.fulfillment-fee",
        AMAZON_ISSUED_STORED_VALUE_CODES: "tag.data-element.amazon-issued-stored-value-codes",
        SELLER_FEES: "tag.data-element.seller-fees",
        APPLICATION_CATEGORY: "tag.data-element.application-category",
        APPLICATION_DATA: "tag.data-element.application-data",
        APPLICATION_DATE_TIME: "tag.data-element.application-date-time",
        APPLICATION_DESCRIPTION: "tag.data-element.application-description",
        APPLICATION_FILENAME: "tag.data-element.application-filename",
        APPLICATION_FLAG: "tag.data-element.application-flag",
        APPLICATION_ID: "tag.data-element.application-id",
        APPLICATION_NAME: "tag.data-element.application-name",
        APPLICATION_NUMBER: "tag.data-element.application-number",
        APPLICATION_PASSWORD: "tag.data-element.application-password",
        APPLICATION_STATUS: "tag.data-element.application-status",
        ATTRIBUTED_REVENUE: "tag.data-element.attributed-revenue",
        BANK_DETAILS: "tag.data-element.bank-details",
        FINANCIAL_ACCOUNT_NUMBER: "tag.data-element.financial-account-number",
        BUYER_REIMBURSEMENT_DATE: "tag.data-element.buyer-reimbursement-date",
        BUYER_REIMBURSEMENT_STATUS: "tag.data-element.buyer-reimbursement-status",
        POSTAL_CODE_NONUCI: "tag.data-element.postal-code-nonuci",
        CODE_COMBINATION_ID: "tag.data-element.code-combination-id",
        COMPANY_NAME: "tag.data-element.company-name",
        COST_PRICE: "tag.data-element.cost-price",
        CREDIT_CARD_ID: "tag.data-element.credit-card-id",
        CREDIT_CARD_NUMBER: "tag.data-element.credit-card-number",
        CREDIT_CARD_PASSWORD_QUESTION: "tag.data-element.credit-card-password-question",
        CREDIT_CARD_PASSWORD_QUESTION_ANSWER: "tag.data-element.credit-card-password-question-answer",
        CREDIT_CARD_SECURITY_CODE: "tag.data-element.credit-card-security-code",
        CURRENCY_CODE: "tag.data-element.currency-code",
        DEPARTMENT_ID: "tag.data-element.department-id",
        EMI_AMOUNT: "tag.data-element.emi-amount",
        EMPLOYEE_ID: "tag.data-element.employee-id",
        EXCHANGE_RATE: "tag.data-element.exchange-rate",
        FINANCE_FILLER_CONFIDENTIAL: "tag.data-element.finance-filler-confidential",
        FINANCE_FILLER_CRITICAL: "tag.data-element.finance-filler-critical",
        FINANCE_FILLER_HIGHLY_CONFIDENTIAL: "tag.data-element.finance-filler-highly-confidential",
        FINANCE_FILLER_PUBLIC: "tag.data-element.finance-filler-public",
        FINANCE_FILLER_RESTRICTED: "tag.data-element.finance-filler-restricted",
        FINANCE_ID: "tag.data-element.finance-id",
        FINANCE_INFO: "tag.data-element.finance-info",
        FINANCIAL_ACCOUNT_NUMBER_PARTIAL: "tag.data-element.financial-account-number-partial",
        GENERAL_LEDGER: "tag.data-element.general-ledger",
        GIFT_CARD_DENOMINATION: "tag.data-element.gift-card-denomination",
        GIFT_CARD_REMAINING_AMOUNT: "tag.data-element.gift-card-remaining-amount",
        GROSS_CONCESSION_VALUE: "tag.data-element.gross-concession-value",
        IMPORT_DUTY_TAX: "tag.data-element.import-duty-tax",
        INVOICE_AMOUNT: "tag.data-element.invoice-amount",
        INVOICE_DATE: "tag.data-element.invoice-date",
        INVOICE_NUMBER: "tag.data-element.invoice-number",
        INVOICE_QUANTITY: "tag.data-element.invoice-quantity",
        ITEM_PRICE: "tag.data-element.item-price",
        ITEM_QUANTITY_PURCHASED: "tag.data-element.item-quantity-purchased",
        JOURNAL_ENTRY_CATEGORY: "tag.data-element.journal-entry-category",
        JOURNAL_ENTRY_HEADER_ID: "tag.data-element.journal-entry-header-id",
        JOURNAL_ENTRY_LINE_NUMBER: "tag.data-element.journal-entry-line-number",
        JOURNAL_ENTRY_SOURCE: "tag.data-element.journal-entry-source",
        JOURNAL_NAME: "tag.data-element.journal-name",
        LANGUAGE_ID: "tag.data-element.language-id",
        LOAN_INTEREST_AMOUNT: "tag.data-element.loan-interest-amount",
        LOAN_INTEREST_RATE: "tag.data-element.loan-interest-rate",
        LOAN_OFFER_AMOUNT: "tag.data-element.loan-offer-amount",
        LOAN_TENURE: "tag.data-element.loan-tenure",
        LOCATION_ID: "tag.data-element.location-id",
        VERIFICATION_AMOUNT: "tag.data-element.verification-amount",
        MONTHLY_INCOME: "tag.data-element.monthly-income",
        NET_CONCESSION_VALUE: "tag.data-element.net-concession-value",
        NET_SALES: "tag.data-element.net-sales",
        DAYS_PAST_DUE: "tag.data-element.days-past-due",
        ORDER_DISCOUNT: "tag.data-element.order-discount",
        ORDER_TAX: "tag.data-element.order-tax",
        ORDER_TOTAL: "tag.data-element.order-total",
        PAYMENT_DYNAMIC_QR_CODE: "tag.data-element.payment-dynamic-qr-code",
        PAYMENT_METHOD: "tag.data-element.payment-method",
        REFUND_AMOUNT: "tag.data-element.refund-amount",
        REWARDS_BALANCE: "tag.data-element.rewards-balance",
        SALES_TAX: "tag.data-element.sales-tax",
        SALES_TAX_EXEMPTION: "tag.data-element.sales-tax-exemption",
        SELLER_FEE_TYPE: "tag.data-element.seller-fee-type",
        PAYMENT_SETTLEMENT_PERIOD_ID: "tag.data-element.payment-settlement-period-id",
        SHIPPING_DISCOUNT: "tag.data-element.shipping-discount",
        STARTING_BALANCE: "tag.data-element.starting-balance",
        SYSTEM_DATE_TIME: "tag.data-element.system-date-time",
        SYSTEM_FILENAME: "tag.data-element.system-filename",
        SYSTEM_FILE_OPERATION: "tag.data-element.system-file-operation",
        SYSTEM_FILE_RECORD: "tag.data-element.system-file-record",
        TAX_RATE: "tag.data-element.tax-rate",
        TAX_TYPE: "tag.data-element.tax-type",
        TOTAL_ITEM_PRICE: "tag.data-element.total-item-price",
        TOTAL_PRODUCT_CHARGE: "tag.data-element.total-product-charge",
        TOTAL_REFUND_AMOUNT: "tag.data-element.total-refund-amount",
        TRANSACTION_AMOUNT: "tag.data-element.transaction-amount",
        UNIT_VALUE: "tag.data-element.unit-value",
        ITEM_QUANTITY_ORDERED: "tag.data-element.item-quantity-ordered",
    },
    FRAUD: {
        FRAUD_DENIED_PARTIES_LIST: "tag.data-element.fraud-denied-parties-list",
        FRAUD_DENIED_PARTIES_LIST_STATUS: "tag.data-element.fraud-denied-parties-list-status",
        FRAUD_DATA_ID: "tag.data-element.fraud-data-id",
    },
    FREETEXT: {
        COMMENT_ID: "tag.data-element.comment-id",
        CUSTOMER_FREETEXT: "tag.data-element.customer-freetext",
        FREETEXT_REVIEW_RATING: "tag.data-element.freetext-review-rating",
        FREETEXT_REVIEW_SEARCH_TERM: "tag.data-element.freetext-review-search-term",
        GIFT_TEXT: "tag.data-element.gift-text",
        MESSAGE_TEXT: "tag.data-element.message-text",
        MESSAGE_ID: "tag.data-element.message-id",
    },
    GOVERNMENT_ID: {
        GOV_ID: "tag.data-element.gov-id",
        GOV_ID_ISSUED_ID_CARD_NUMBER: "tag.data-element.gov-id-issued-id-card-number",
        GOV_ID_GST_NUMBER: "tag.data-element.gov-id-gst-number",
        GOV_ID_PERMANENT_ACCOUNT_NUMBER: "tag.data-element.gov-id-permanent-account-number",
        GOV_ID_SSN_NUMBER_PARTIAL: "tag.data-element.gov-id-ssn-number-partial",
    },
    HEALTH: {
        ADMISSION_DATE: "tag.data-element.admission-date",
        AGE: "tag.data-element.age",
        AHS_SUBSCRIPTION_ID: "tag.data-element.ahs-subscription-id",
        AHS_PROGRAM: "tag.data-element.ahs-program",
        BIOMETRIC_ID: "tag.data-element.biometric-id",
        CONVERSATION_ID: "tag.data-element.conversation-id",
        CPT_CODE: "tag.data-element.cpt-code",
        DEATH_DATE: "tag.data-element.death-date",
        DISCHARGE_DATE: "tag.data-element.discharge-date",
        DISEASE_RELATED_DATA: "tag.data-element.disease-related-data",
        DISPENSE_ID: "tag.data-element.dispense-id",
        DRUG_DOSAGE: "tag.data-element.drug-dosage",
        DRUG_ID: "tag.data-element.drug-id",
        GENDER: "tag.data-element.gender",
        HALO_SLEEP_SESSION_ID: "tag.data-element.halo-sleep-session-id",
        HEALTH_ACTIVITY_ID: "tag.data-element.health-activity-id",
        DISEASE_NAME: "tag.data-element.disease-name",
        HEALTHPLAN_ID: "tag.data-element.healthplan-id",
        HEALTH_TRACKING_DATA: "tag.data-element.health-tracking-data",
        ICD_CODE: "tag.data-element.icd-code",
        INSURANCE_BIN: "tag.data-element.insurance-bin",
        INSURANCE_GROUP_ID: "tag.data-element.insurance-group-id",
        INSURANCE_PCN: "tag.data-element.insurance-pcn",
        LANGUAGE_PREFERENCE: "tag.data-element.language-preference",
        MARITAL_STATUS: "tag.data-element.marital-status",
        MEDICAL_CLAIM_GROUP_ID: "tag.data-element.medical-claim-group-id",
        MEDICAL_CLAIM_ID: "tag.data-element.medical-claim-id",
        MEDICAL_FACILITY_ID: "tag.data-element.medical-facility-id",
        MEDICAL_LICENSE_NUMBER: "tag.data-element.medical-license-number",
        MEDICAL_RECORD_NUMBER: "tag.data-element.medical-record-number",
        MEDICATION_NAME: "tag.data-element.medication-name",
        NDC_CODE: "tag.data-element.ndc-code",
        PRESCRIPTION_ID: "tag.data-element.prescription-id",
        RACE: "tag.data-element.race",
        SEX_AT_BIRTH: "tag.data-element.sex-at-birth",
        SOCIAL_MEDIA_ID: "tag.data-element.social-media-id",
        SOCIAL_MEDIA_TOKEN: "tag.data-element.social-media-token",
        TREATMENT_PLAN_ID: "tag.data-element.treatment-plan-id",
        VISIT_ID: "tag.data-element.visit-id",
        VISUAL_BODY_COMPOSITION_SCORE: "tag.data-element.visual-body-composition-score",
        WEB_REQUEST_ID: "tag.data-element.web-request-id",
        BIRTH_YEAR: "tag.data-element.birth-year",
    },
    IDENTITY: {
        CUSTOMER_ACCOUNT_POOL: "tag.data-element.customer-account-pool",
        CUSTOMER_ID: "tag.data-element.customer-id",
        CUSTOMER_ID_CLEAR: "tag.data-element.customer-id.clear",
        CUSTOMER_ID_OBFUSCATED: "tag.data-element.customer-id.obfuscated",
        CUSTOMER_ID_ACI: "tag.data-element.customer-id.aci",
        DATE_OF_BIRTH: "tag.data-element.date-of-birth",
        DIRECTED_CUSTOMER_ID: "tag.data-element.directed-customer-id",
        DIRECTED_MERCHANT_ID: "tag.data-element.directed-merchant-id",
        DIRECTED_PERSON_CUSTOMER_ID: "tag.data-element.directed-person-customer-id",
        DUE_DILIGENCE_IDENTIFIER: "tag.data-element.due-diligence-identifier",
        EMAIL: "tag.data-element.email",
        EMAIL_DOMAIN: "tag.data-element.email-domain",
        EMAIL_ID: "tag.data-element.email-id",
        ENCYRPTED_CUSTOMER_ID: "tag.data-element.encyrpted-customer-id",
        ENCRYPTED_MERCHANT_ID: "tag.data-element.encrypted-merchant-id",
        INDIVIDUAL_FIRST_NAME: "tag.data-element.individual-first-name",
        GOV_ID_AADHAR_NUMBER: "tag.data-element.gov-id-aadhar-number",
        GOV_ID_DRIVER_LICENSE_NUMBER: "tag.data-element.gov-id-driver-license-number",
        GOV_ID_PASSPORT_NUMBER: "tag.data-element.gov-id-passport-number",
        GOV_ID_SSN_NUMBER: "tag.data-element.gov-id-ssn-number",
        GOV_ID_TAX_ID: "tag.data-element.gov-id-tax-id",
        GOV_ID_VEHICLE_ID_NUMBER: "tag.data-element.gov-id-vehicle-id-number",
        HASH_VALUE: "tag.data-element.hash-value",
        HOUSEHOLD_ID: "tag.data-element.household-id",
        INVENTORY_OWNER_GROUP_ID: "tag.data-element.inventory-owner-group-id",
        INDIVIDUAL_LAST_NAME: "tag.data-element.individual-last-name",
        LEGAL_NAME: "tag.data-element.legal-name",
        MERCHANT_ID: "tag.data-element.merchant-id",
        MERCHANT_ID_CLEAR: "tag.data-element.merchant-id.clear",
        MERCHANT_ID_OBFUSCATED: "tag.data-element.merchant-id.obfuscated",
        MERCHANT_ID_ACI: "tag.data-element.merchant-id.aci",
        INDIVIDUAL_FULL_NAME: "tag.data-element.individual-full-name",
        INDIVIDUAL_NICKNAME: "tag.data-element.individual-nickname",
        PASSWORD: "tag.data-element.password",
        PASSWORD_AUTH_CODE: "tag.data-element.password-auth-code",
        PAYEE_ID: "tag.data-element.payee-id",
        PAYEE_SITE_ID: "tag.data-element.payee-site-id",
        PROFILE_ID: "tag.data-element.profile-id",
        PROFILE_ID_ACI: "tag.data-element.profile-id.aci",
        PROFILE_ID_CLEAR: "tag.data-element.profile-id.clear",
        PROFILE_ID_OBFUSCATED: "tag.data-element.profile-id.obfuscated",
        SELLER_ID: "tag.data-element.seller-id",
        SENSITIVE_DATA_HASH: "tag.data-element.sensitive-data-hash",
        SHIPMENT_ID: "tag.data-element.shipment-id",
        SPEAKER_CUSTOMER_ID: "tag.data-element.speaker-customer-id",
        SUPPLY_MANAGEMENT_ORGANIZATION_ID: "tag.data-element.supply-management-organization-id",
        URL_ADDRESS: "tag.data-element.url-address",
        VAT_ID: "tag.data-element.vat-id",
        VEHICLE_PLATE_NUMBER: "tag.data-element.vehicle-plate-number",
        VENDOR_CODE: "tag.data-element.vendor-code",
        VENDOR_ID: "tag.data-element.vendor-id",
    },
    IDENTITY_ADDRESS: {
        ADDRESS_ID: "tag.data-element.address-id",
        BUILDING_IDENTIFIER: "tag.data-element.building-identifier",
        CITY: "tag.data-element.city",
        COUNTRY_CODE: "tag.data-element.country-code",
        COUNTRY_NAME: "tag.data-element.country-name",
        COUNTRY: "tag.data-element.country",
        DISTRICT: "tag.data-element.district",
        GEOLOCATION: "tag.data-element.geolocation",
        MAP_DATA: "tag.data-element.map-data",
        ADDRESS: "tag.data-element.address",
        ADDRESS_POBOX: "tag.data-element.address-pobox",
        PROVINCE: "tag.data-element.province",
        SESSION_ID: "tag.data-element.session-id",
        SESSION_ID_CLEAR: "tag.data-element.session-id.clear",
        SESSION_ID_OBFUSCATED: "tag.data-element.session-id.obfuscated",
        STATE: "tag.data-element.state",
        ADDRESS_LINE_1: "tag.data-element.address-line-1",
        ADDRESS_LINE_2: "tag.data-element.address-line-2",
        ADDRESS_LINE_3: "tag.data-element.address-line-3",
        POSTAL_CODE: "tag.data-element.postal-code",
    },
    IDENTITY_PASSWORD: {
        PASSWORD_RECOVERY_QUESTION: "tag.data-element.password-recovery-question",
        PASSWORD_RECOVERY_QUESTION_ANSWER: "tag.data-element.password-recovery-question-answer",
    },
    MEDIA: {
        FOOTAGE_FOR_COMPUTER_VISION_APPLICATIONS: "tag.data-element.footage-for-computer-vision-applications",
        INDIVIDUAL_PHOTOS: "tag.data-element.individual-photos",
        SCREEN_RECORDING: "tag.data-element.screen-recording",
        SECURITY_CAMERA_FOOTAGE: "tag.data-element.security-camera-footage",
        VIDEO_RECORDING: "tag.data-element.video-recording",
        VOICE_RECORDING: "tag.data-element.voice-recording",
    },
    ORDERS: {
        ASIN: "tag.data-element.asin",
        BWP_ORDER_ID: "tag.data-element.bwp-order-id",
        CATALOG_CONTRIBUTOR_ID: "tag.data-element.catalog-contributor-id",
        CATALOG_CONTRIBUTOR_ID_CLEAR: "tag.data-element.catalog-contributor-id.clear",
        CATALOG_CONTRIBUTOR_ID_OBFUSCATED: "tag.data-element.catalog-contributor-id.obfuscated",
        CATALOG_CONTRIBUTOR_ID_ACI: "tag.data-element.catalog-contributor-id.aci",
        CONCESSION_ID: "tag.data-element.concession-id",
        CUSTOMER_PURCHASE_ID: "tag.data-element.customer-purchase-id",
        DEAL_ID: "tag.data-element.deal-id",
        DEFECT_ID: "tag.data-element.defect-id",
        GL_PRODUCT_GROUP: "tag.data-element.gl-product-group",
        GL_PRODUCT_GROUP_DESC: "tag.data-element.gl-product-group-desc",
        INBOUND_SHIPMENT_ID: "tag.data-element.inbound-shipment-id",
        INBOUND_SHIPMENT_ITEM_ID: "tag.data-element.inbound-shipment-item-id",
        IS_3P_PARTNER: "tag.data-element.is-3p-partner",
        IS_3P: "tag.data-element.is-3p",
        MARKETPLACE_ID: "tag.data-element.marketplace-id",
        MARKETPLACE_ID_CLEAR: "tag.data-element.marketplace-id.clear",
        MARKETPLACE_ID_OBFUSCATED: "tag.data-element.marketplace-id.obfuscated",
        MERCHANT_ID_ACI: "tag.data-element.merchant-id-aci",
        OFFER_LISTING_ID: "tag.data-element.offer-listing-id",
        ORDER_ID: "tag.data-element.order-id",
        ORDER_ITEM_ID: "tag.data-element.order-item-id",
        DELIVERY_PACKET_ID: "tag.data-element.delivery-packet-id",
        PARTNER_ACCOUNT_ID_ACI: "tag.data-element.partner-account-id-aci",
        PROMOTION_ID: "tag.data-element.promotion-id",
        PROMOTION_ID_CLEAR: "tag.data-element.promotion-id.clear",
        PROMOTION_ID_OBFUSCATED: "tag.data-element.promotion-id.obfuscated",
        PURCHASE_ORDER_ID: "tag.data-element.purchase-order-id",
        PURCHASE_ORDER_ITEM_ID: "tag.data-element.purchase-order-item-id",
        RETURN_ID: "tag.data-element.return-id",
        RETURN_ITEM_ID: "tag.data-element.return-item-id",
        SP_ACCOUNT_ID: "tag.data-element.sp-account-id",
        SP_ACCOUNT_ID_CLEAR: "tag.data-element.sp-account-id.clear",
        SP_ACCOUNT_ID_OBFUSCATED: "tag.data-element.sp-account-id.obfuscated",
        SP_ACCOUNT_ID_ACI: "tag.data-element.sp-account-id.aci",
        SHIPMENT_ITEM_ID: "tag.data-element.shipment-item-id",
    },
    PRIVACY_INFRASTRUCTURE: {
        DOCUMENT_ID: "tag.data-element.document-id",
        POLICY_APPLICABILITY_BITMAP: "tag.data-element.policy-applicability-bitmap",
        POLICY_ENFORCEMENT_BITMAP: "tag.data-element.policy-enforcement-bitmap",
    },
    SECURITY: {
        TWO_FACTOR_AUTH_CODE: "tag.data-element.two-factor-auth-code",
    },
    SELLER: {
        SELLER_INVESTIGATION_CASE_ID: "tag.data-element.seller-investigation-case-id",
    },
    SELLING_PARTNER_SERVICES: {
        IS_AMAZON_PARTNER: "tag.data-element.is-amazon-partner",
        IS_RETAIL_PARTNER: "tag.data-element.is-retail-partner",
    },
    SERVICELOG: {
        DIAGNOSTIC_LOG_TEXT: "tag.data-element.diagnostic-log-text",
    },
    TRANSPORTATION: {
        CARRIER_OPERATION_MANAGEMENT_PLATFORM_ID: "tag.data-element.carrier-operation-management-platform-id",
        PROVIDER_ID: "tag.data-element.provider-id",
        TRANSPORTER_ID: "tag.data-element.transporter-id",
        VEHICLE_SAFETY_DATA: "tag.data-element.vehicle-safety-data",
    },
} as const;

import { SelectProps } from "@amzn/awsui-components-react";

/**
 * Turn a tag id (e.g. "tag.data-element.advertiser-id.clear") into a readable
 * label (e.g. "Advertiser Id Clear") for display in a dropdown. The value of
 * each option remains the exact tag id expected by BDT tooling.
 */
const prettifyTagId = (tagId: string): string => {
    const last = tagId.split(".").slice(2).join(".");
    return last
        .split(/[.-]/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

/**
 * Cloudscape Select options for the Data Classification Level dropdown, ordered
 * least to most sensitive (matching DATA_CLASSIFICATION_LEVEL insertion order).
 */
export const DATA_CLASSIFICATION_LEVEL_OPTIONS: SelectProps.Option[] =
    Object.values(DATA_CLASSIFICATION_LEVEL).map((tagId) => ({
        label: prettifyTagId(tagId),
        value: tagId,
    }));

/**
 * Cloudscape Select option groups for the Data Element dropdown, grouped by the
 * wiki Data Category. Each leaf option's value is the exact data-element tag id.
 */
export const DATA_ELEMENT_TAG_OPTION_GROUPS: SelectProps.OptionGroup[] =
    Object.entries(DATA_ELEMENT_TAGS).map(([category, tags]) => ({
        label: category
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" "),
        options: Object.values(tags as Record<string, string>).map((tagId) => ({
            label: prettifyTagId(tagId),
            value: tagId,
        })),
    }));
